// The handoff guide's steps, in the order the Play Console asks for things on
// a brand-new app: create it, answer the App content declarations, store
// settings, the listing, translations, then the release. Every answer comes
// from the publish template marked for handoff — the same one the Play runner
// replays and the Data safety CSV comes from — so a person following this page
// lands the app exactly where the runner would.
import { useState, type ReactNode } from 'react';
import { handoffFileUrl, type HandoffListing, type HandoffPackage } from '@/api/app-handoff';
import { buildZip, saveBlob, type ZipEntry } from '@/lib/icon-export';
import { playLocaleName } from '@/lib/playLocales';
import {
  AD_ID_PURPOSES, FINANCE_NONE, HEALTH_NONE, GAMBLING_KINDS, IARC_CATEGORIES, IARC_OTHER,
  IARC_OTHER_APP_QUESTIONS, PLAY_CATEGORIES,
  readAppContent, readContentRating, readListingDefaults,
} from '@/lib/play-template';
import {
  Act, Actions, AnswerTable, Callout, CopyField, DownloadButton, FileTile, IconCheck, IconRight, Pick,
} from './HandoffUi';

/** Play's limits; the server's `fits` flag uses the same numbers. */
const LIMIT = { title: 30, short: 80, full: 4000 } as const;

export interface GuideStep {
  id: string;
  title: string;
  /** Where the step happens in the console's left menu. */
  where?: string[];
  /** One line under the title: why this step, or what it ends with. */
  intro?: string;
  body: ReactNode;
}

type Shot = HandoffPackage['screenshots'][number];

const localeLabel = (lang: string) => `${playLocaleName(lang) ?? lang} – ${lang}`;
const ageLabel = (band: string) => (band === '18+' ? '18 and over' : band.replace('-', '–'));
const ext = (contentType: string) =>
  (contentType === 'image/jpeg' ? 'jpg' : contentType === 'image/webp' ? 'webp' : 'png');

function listingText(l: HandoffListing): string {
  return `App name:\n${l.title}\n\nShort description:\n${l.short_description}\n\nFull description:\n${l.full_description}\n`;
}

/**
 * Fetches the files and zips them in the browser — the API serves every file
 * on its own, and a page cannot fire a dozen downloads at once without the
 * browser blocking all but the first.
 */
async function zipAndSave(name: string, files: { name: string; url: string }[], texts: ZipEntry[] = []) {
  const entries: ZipEntry[] = [...texts];
  for (const f of files) {
    const res = await fetch(f.url);
    if (!res.ok) throw new Error(`${f.name}: ${res.status}`);
    entries.push({ name: f.name, data: new Uint8Array(await res.arrayBuffer()) });
  }
  saveBlob(buildZip(entries), name);
}

function useZip() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function run(name: string, files: { name: string; url: string }[], texts?: ZipEntry[]) {
    setBusy(true);
    setError('');
    try {
      await zipAndSave(name, files, texts);
    } catch {
      setError('Could not build the archive. Download the files one by one instead.');
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

// ---------------------------------------------------------------------------
// Shared blocks

function ScreenshotSet({ token, shots, lang, bundle }: { token: string; shots: Shot[]; lang: string; bundle: string }) {
  const zip = useZip();
  if (shots.length === 0) {
    return (
      <Callout>
        No screenshots made for {playLocaleName(lang) ?? lang}. Skip them — Play shows the default language&apos;s
        screenshots for this language.
      </Callout>
    );
  }
  const files = shots.map((s, i) => ({
    name: `${bundle}-${lang}-${String(i + 1).padStart(2, '0')}.${ext(s.content_type)}`,
    url: handoffFileUrl.screenshot(token, s.id),
  }));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
        {shots.map((s, i) => (
          <a key={s.id} href={handoffFileUrl.screenshot(token, s.id)} title={`Download screenshot ${i + 1}`}
            className="group relative block overflow-hidden rounded-md border border-border">
            <img src={handoffFileUrl.screenshot(token, s.id)} alt={s.caption ?? `Screenshot ${i + 1}`}
              loading="lazy" className="aspect-[9/16] w-full object-cover group-hover:opacity-90" />
            <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 text-[11px] font-semibold text-white">{i + 1}</span>
          </a>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <DownloadButton busy={zip.busy} onClick={() => zip.run(`${bundle}-${lang}-screenshots.zip`, files)}>
          All {shots.length} screenshots (.zip)
        </DownloadButton>
        <span className="text-xs text-content-subtle">Upload them in this order: 1, 2, 3…</span>
      </div>
      {zip.error && <p className="text-xs text-danger">{zip.error}</p>}
    </div>
  );
}

function ListingTexts({ listing, appName }: { listing: HandoffListing; appName: string }) {
  return (
    <>
      <CopyField label="App name" value={listing.title || appName} limit={LIMIT.title} />
      <CopyField label="Short description" value={listing.short_description} limit={LIMIT.short} multiline />
      <CopyField label="Full description" value={listing.full_description} limit={LIMIT.full} multiline />
    </>
  );
}

function BuildDownload({ token, pkg }: { token: string; pkg: HandoffPackage }) {
  const { build } = pkg;
  if (!build) {
    return (
      <Callout tone="warning" title="No build in this link">
        The app file (.aab) was not attached. Ask whoever sent the link for one that includes the build.
      </Callout>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content">{build.filename}</p>
          <p className="text-xs text-content-subtle">
            Version {build.version_name} ({build.version_code}) · signed, upload as is
          </p>
        </div>
        <DownloadButton primary href={handoffFileUrl.build(token)}>Download</DownloadButton>
      </div>
      {build.build_type !== 'aab' && (
        <Callout tone="warning">
          This is an {build.build_type.toUpperCase()}. Play only accepts an .aab for a new app — ask the sender for an AAB.
        </Callout>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps with their own state

function StartStep({ token, pkg }: { token: string; pkg: HandoffPackage }) {
  const zip = useZip();
  const bundle = pkg.app.bundle_id ?? 'app';
  const shotsByLang = new Map<string, Shot[]>();
  for (const s of pkg.screenshots) shotsByLang.set(s.lang, [...(shotsByLang.get(s.lang) ?? []), s]);

  function downloadAll() {
    const files: { name: string; url: string }[] = [];
    if (pkg.has_icon) files.push({ name: `${bundle}/icon-512.png`, url: handoffFileUrl.icon(token) });
    if (pkg.has_feature_graphic) files.push({ name: `${bundle}/feature-graphic-1024x500.png`, url: handoffFileUrl.featureGraphic(token) });
    for (const [lang, shots] of shotsByLang) {
      shots.forEach((s, i) => files.push({
        name: `${bundle}/screenshots/${lang}/${String(i + 1).padStart(2, '0')}.${ext(s.content_type)}`,
        url: handoffFileUrl.screenshot(token, s.id),
      }));
    }
    if (pkg.has_data_safety_csv) files.push({ name: `${bundle}/data-safety.csv`, url: handoffFileUrl.dataSafetyCsv(token) });
    const enc = new TextEncoder();
    const texts = pkg.listings.map((l) => ({ name: `${bundle}/texts/${l.lang}.txt`, data: enc.encode(listingText(l)) }));
    void zip.run(`${bundle}.zip`, files, texts);
  }

  const items = [
    pkg.build ? `The app file (${pkg.build.build_type.toUpperCase()} ${pkg.build.version_name})` : null,
    `Store texts in ${pkg.listings.length} language${pkg.listings.length > 1 ? 's' : ''}`,
    pkg.has_icon ? 'App icon' : null,
    pkg.has_feature_graphic ? 'Feature graphic' : null,
    pkg.screenshots.length ? `${pkg.screenshots.length} screenshots` : null,
    pkg.has_data_safety_csv ? 'Data safety file' : null,
    pkg.answers ? 'Every answer for the console forms' : null,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-5">
      {pkg.note && <Callout title="Message from the sender"><span className="whitespace-pre-wrap">{pkg.note}</span></Callout>}

      <div>
        <p className="mb-2 text-sm font-medium text-content">In this package</p>
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {items.map((it) => (
            <li key={it} className="flex items-center gap-2 text-sm text-content">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success-soft text-success">
                <IconCheck className="h-3 w-3" />
              </span>
              {it}
            </li>
          ))}
        </ul>
      </div>

      <Actions>
        <Act title="Open Play Console on a computer and sign in to the developer account the app goes to." />
        <Act title="Open this page on the same computer. Copy buttons put the text on this device's clipboard." />
        <Act title="Download everything now — the link stops working after a while.">
          <div className="flex flex-wrap gap-2">
            <DownloadButton primary busy={zip.busy} onClick={downloadAll}>All images, texts &amp; files (.zip)</DownloadButton>
            {pkg.build && <DownloadButton href={handoffFileUrl.build(token)}>App file ({pkg.build.build_type})</DownloadButton>}
          </div>
          {zip.error && <p className="text-xs text-danger">{zip.error}</p>}
        </Act>
        <Act title="Go through the steps one by one. Press “Done, next” at the bottom of each step — your progress is saved in this browser." />
      </Actions>
    </div>
  );
}

function TranslationsStep({ token, pkg, extra }: { token: string; pkg: HandoffPackage; extra: HandoffListing[] }) {
  const [lang, setLang] = useState(extra[0]!.lang);
  const pos = Math.max(0, extra.findIndex((l) => l.lang === lang));
  const listing = extra[pos]!;
  const bundle = pkg.app.bundle_id ?? 'app';
  return (
    <div className="space-y-5">
      <Actions>
        <Act title={<>On the default store listing page press <b>Manage translations</b> › <b>Add your own translation text</b>.</>} />
        <Act title={`Tick these ${extra.length} languages and press Apply:`}>
          <div className="flex flex-wrap gap-1.5">
            {extra.map((l) => (
              <span key={l.lang} className="rounded-md bg-surface-muted px-2 py-1 text-xs text-content">{localeLabel(l.lang)}</span>
            ))}
          </div>
        </Act>
        <Act title="Switch the language at the top of the listing page, paste that language's texts and upload its screenshots. Save, then the next language." />
      </Actions>

      <div className="rounded-xl border border-border bg-surface-sunken/60 p-3 sm:p-4">
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-content-muted">Language</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)}
            className="min-h-[40px] w-full rounded-lg border border-border bg-surface px-3 text-sm text-content sm:w-80">
            {extra.map((l, i) => (
              <option key={l.lang} value={l.lang}>{i + 1}. {localeLabel(l.lang)}</option>
            ))}
          </select>
        </label>
        <div className="space-y-3">
          {!listing.fits && (
            <Callout tone="warning">This translation is over Play&apos;s length limits — see the red counters below.</Callout>
          )}
          <ListingTexts listing={listing} appName={pkg.app.name} />
          <p className="pt-2 text-sm font-medium text-content">Phone screenshots</p>
          <ScreenshotSet token={token} shots={pkg.screenshots.filter((s) => s.lang === lang)} lang={lang} bundle={bundle} />
          {pos < extra.length - 1 && (
            <div className="flex justify-end pt-2">
              <button type="button" onClick={() => setLang(extra[pos + 1]!.lang)}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-border bg-surface px-4 text-sm font-medium text-content hover:bg-surface-muted">
                Next language: {playLocaleName(extra[pos + 1]!.lang) ?? extra[pos + 1]!.lang} <IconRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Every step of the guide for this package; the last one is always "done". */
export function buildSteps(token: string, pkg: HandoffPackage): GuideStep[] {
  const app = readAppContent(pkg.answers?.app_content);
  const rating = readContentRating(pkg.answers?.content_rating);
  const defaults = readListingDefaults(pkg.answers?.listing_defaults);
  const source = pkg.listings.find((l) => l.is_source) ?? pkg.listings[0]!;
  const extra = pkg.listings.filter((l) => l !== source);
  const bundle = pkg.app.bundle_id ?? 'app';
  const isGame = app.app_type === 'game';
  const category = PLAY_CATEGORIES.find((c) => c.id === defaults.category)?.label ?? defaults.category;
  const notes = `<${source.lang}>\n${defaults.release_notes || 'Initial release.'}\n</${source.lang}>`;
  const sourceShots = pkg.screenshots.filter((s) => s.lang === source.lang);

  const start: GuideStep = {
    id: 'start',
    title: 'Before you start',
    intro: 'What this link contains and how to use this page.',
    body: <StartStep token={token} pkg={pkg} />,
  };

  const create: GuideStep = {
    id: 'create',
    title: 'Create the app',
    where: ['Home', 'Create app'],
    intro: 'The first form of a new app.',
    body: (
      <Actions>
        <Act title={<>Press <b>Create app</b>.</>} />
        <Act title="App name">
          <CopyField label="App name" value={source.title || pkg.app.name} limit={LIMIT.title} />
        </Act>
        <Act title="Package name (if the form asks for it)">
          <CopyField label="Package name" value={pkg.app.bundle_id} />
        </Act>
        <Act title="Default language">
          <Pick q="Default language" a={localeLabel(source.lang)} />
        </Act>
        <Act title="App or game, free or paid">
          <Pick q="App or game" a={isGame ? 'Game' : 'App'} />
          <Pick q="Free or paid" a={app.pricing === 'free' ? 'Free' : 'Paid'} />
        </Act>
        <Act title="Declarations: tick every checkbox">
          <Pick q="Declarations" a={['Developer Program Policies', 'Play App Signing', 'US export laws']}
            sub="The Play App Signing box only appears for the first app on an account." />
        </Act>
        <Act title={<>Press <b>Create app</b> at the bottom.</>} />
      </Actions>
    ),
  };

  const privacy: GuideStep = {
    id: 'privacy',
    title: 'Privacy policy',
    where: ['Policy and programs', 'App content', 'Privacy policy'],
    intro: 'From here on each step is also a task under Dashboard › “Set up your app” — clicking the task opens the same page.',
    body: (
      <Actions>
        <Act title="Paste the link into “Privacy policy URL”.">
          <CopyField label="Privacy policy URL" value={pkg.app.privacy_policy_url} />
        </Act>
        <Act title={<>Press <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const access: GuideStep = {
    id: 'access',
    title: 'App access',
    where: ['Policy and programs', 'App content', 'App access'],
    body: (
      <Actions>
        <Act title="Choose:">
          <Pick q="App access" a={app.app_access === 'unrestricted'
            ? 'All functionality in my app is available without any access restrictions'
            : 'All or some functionality is restricted'} />
          {app.app_access !== 'unrestricted' && (
            <Callout tone="warning">This app needs test login details. Ask the sender for them.</Callout>
          )}
        </Act>
        <Act title={<>Press <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const ads: GuideStep = {
    id: 'ads',
    title: 'Ads',
    where: ['Policy and programs', 'App content', 'Ads'],
    body: (
      <Actions>
        <Act title="Choose:">
          <Pick q="Does your app contain ads?" a={app.contains_ads ? 'Yes, my app contains ads' : 'No, my app does not contain ads'} />
        </Act>
        <Act title={<>Press <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const g = rating.answers.gambling;
  const ratingRows = rating.category === 'game'
    ? [
      ...IARC_CATEGORIES.map((c) => ({ q: c.label, a: rating.answers[c.id] ? 'Yes' : 'No', yes: rating.answers[c.id] })),
      { q: 'Gambling', a: g.present ? 'Yes' : 'No', yes: g.present },
      ...IARC_OTHER.map((c) => ({ q: c.label, a: rating.answers.other[c.id] ? 'Yes' : 'No', yes: rating.answers.other[c.id] })),
    ]
    : IARC_OTHER_APP_QUESTIONS.map((x) => ({ q: `${x.card}: ${x.question}`, a: 'No' }));

  const contentRating: GuideStep = {
    id: 'rating',
    title: 'Content rating',
    where: ['Policy and programs', 'App content', 'Content ratings'],
    intro: 'A short questionnaire. The rating is issued right after you submit.',
    body: (
      <Actions>
        <Act title={<>Press <b>Start questionnaire</b>.</>} />
        <Act title="Email address: type your own — the rating certificate is sent there." />
        <Act title="Category">
          <Pick q="Category" a={rating.category === 'game' ? 'Game' : 'All Other App Types'} />
        </Act>
        <Act title={<>Press <b>Next</b> and answer:</>}>
          <AnswerTable rows={ratingRows} />
          {rating.category === 'game' && g.present && (
            <Pick q="Gambling — what kind" a={g.kinds.map((k) => GAMBLING_KINDS.find((x) => x.id === k)?.label ?? k)}
              sub={g.central ? 'Gambling is central to the game: Yes' : 'Gambling is central to the game: No'} />
          )}
        </Act>
        <Act title={<>Press <b>Save</b>, then <b>Next</b>, then <b>Submit</b>.</>} />
      </Actions>
    ),
  };

  const audience: GuideStep = {
    id: 'audience',
    title: 'Target audience',
    where: ['Policy and programs', 'App content', 'Target audience and content'],
    body: (
      <Actions>
        <Act title="Tick only this age group:">
          <Pick q="Target age" a={app.target_age_groups.map(ageLabel)} />
        </Act>
        <Act title="If the console asks whether the app could unintentionally appeal to children:">
          <Pick q="Appeal to children" a="No" />
        </Act>
        <Act title={<>Press <b>Next</b> until the end, then <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const dataSafety: GuideStep = {
    id: 'datasafety',
    title: 'Data safety',
    where: ['Policy and programs', 'App content', 'Data safety'],
    intro: 'All answers are in one file — no need to fill the form by hand.',
    body: pkg.has_data_safety_csv ? (
      <Actions>
        <Act title="Download the file:">
          <DownloadButton primary href={handoffFileUrl.dataSafetyCsv(token)}>data-safety.csv</DownloadButton>
        </Act>
        <Act title={<>On the Data safety page press <b>Import from CSV</b> and pick the file.</>} />
        <Act title={<>Check the preview and press <b>Save</b>.</>} />
      </Actions>
    ) : (
      <Callout tone="warning" title="No Data safety file in this link">Ask the sender for the answers before you fill this form.</Callout>
    ),
  };

  const adPurposes = app.advertising_id.purposes.map((p) => AD_ID_PURPOSES.find((x) => x.id === p)?.label ?? p);
  const declarations: GuideStep = {
    id: 'declarations',
    title: 'Other declarations',
    where: ['Policy and programs', 'App content'],
    intro: `${isGame ? 'Four' : 'Five'} short forms on the same App content page. Save each one.`,
    body: (
      <Actions>
        <Act title="Government apps">
          <Pick q="Is your app developed by or on behalf of a government?" a={app.government_app ? 'Yes' : 'No'} />
        </Act>
        <Act title="Financial features">
          <Pick q="Financial features" a={app.financial_features.map((f) =>
            (f === FINANCE_NONE ? "My app doesn't provide any financial features" : f))} />
        </Act>
        <Act title="Health apps">
          <Pick q="Health features" a={app.health_categories.map((h) =>
            (h === HEALTH_NONE ? 'My app does not have any health features' : h))} />
        </Act>
        <Act title="Advertising ID">
          <Pick q="Does your app use advertising ID?" a={app.advertising_id.used ? 'Yes' : 'No'} />
          {app.advertising_id.used && adPurposes.length > 0 && (
            <Pick q="Why does your app need to use advertising ID? (tick these)" a={adPurposes} />
          )}
        </Act>
        {!isGame && (
          <Act title="News apps">
            <Pick q="Is your app a news app?" a={app.news_app ? 'Yes' : 'No'} />
          </Act>
        )}
      </Actions>
    ),
  };

  const settings: GuideStep = {
    id: 'settings',
    title: 'Store settings',
    where: ['Grow users', 'Store presence', 'Store settings'],
    intro: 'Category and contact details.',
    body: (
      <Actions>
        <Act title={<>Under <b>App category</b> press Edit and choose:</>}>
          <Pick q="App or game" a={isGame ? 'Game' : 'App'} />
          <Pick q="Category" a={category} />
          <p className="text-xs text-content-subtle">Tags are optional — skip them. Press Save.</p>
        </Act>
        <Act title={<>Under <b>Store listing contact details</b> press Edit:</>}>
          <CopyField label="Email address" value={pkg.app.contact_email} />
          {defaults.website && <CopyField label="Website" value={defaults.website} />}
          {defaults.phone
            ? <CopyField label="Phone number" value={defaults.phone} />
            : <p className="text-xs text-content-subtle">Phone number and website: leave empty.</p>}
        </Act>
        <Act title={<>Press <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const listing: GuideStep = {
    id: 'listing',
    title: 'Main store listing',
    where: ['Grow users', 'Store presence', 'Store listings', 'Default store listing'],
    intro: `What people see on Google Play, in ${playLocaleName(source.lang) ?? source.lang}.`,
    body: (
      <Actions>
        <Act title="App details — paste each text into its field:">
          {!source.fits && <Callout tone="warning">This text is over Play&apos;s length limits — see the red counters below.</Callout>}
          <ListingTexts listing={source} appName={pkg.app.name} />
        </Act>
        <Act title="Graphics — upload the icon and the feature graphic:">
          {pkg.has_icon
            ? <FileTile title="App icon" sub="512 × 512 PNG" href={handoffFileUrl.icon(token)} preview={handoffFileUrl.icon(token)} />
            : <Callout tone="warning">No icon in this link — ask the sender.</Callout>}
          {pkg.has_feature_graphic
            ? <FileTile wide title="Feature graphic" sub="1024 × 500" href={handoffFileUrl.featureGraphic(token)} preview={handoffFileUrl.featureGraphic(token)} />
            : <Callout tone="warning">No feature graphic in this link — Play will not publish without one. Ask the sender.</Callout>}
        </Act>
        <Act title="Phone screenshots — upload in this order:">
          <ScreenshotSet token={token} shots={sourceShots} lang={source.lang} bundle={bundle} />
          <p className="text-xs text-content-subtle">Tablet screenshots and video are optional — leave them empty.</p>
        </Act>
        <Act title={<>Press <b>Save</b>.</>} />
      </Actions>
    ),
  };

  const translations: GuideStep | null = extra.length > 0 ? {
    id: 'translations',
    title: 'Translations',
    where: ['Grow users', 'Store presence', 'Store listings', 'Default store listing'],
    intro: `The same listing in ${extra.length} more language${extra.length > 1 ? 's' : ''}.`,
    body: <TranslationsStep token={token} pkg={pkg} extra={extra} />,
  } : null;

  const release: GuideStep = {
    id: 'release',
    title: 'Release',
    where: ['Test and release', 'Production'],
    intro: 'Upload the app file and send everything for review.',
    body: (
      <Actions>
        <Act title={<>Open the <b>Countries / regions</b> tab › <b>Add countries / regions</b> and tick the countries you were told to. Save.</>}>
          <p className="text-xs text-content-subtle">Nobody told you which? Ask the sender before you publish.</p>
        </Act>
        <Act title={<>Press <b>Create new release</b>. If Play asks about app signing, keep the option Google recommends.</>} />
        <Act title="Upload the app file:">
          <BuildDownload token={token} pkg={pkg} />
        </Act>
        <Act title="Release name — leave what Play filled in." />
        <Act title="Release notes — replace the whole field with:">
          <CopyField label="Release notes" value={notes} multiline />
        </Act>
        <Act title={<>Press <b>Next</b>, then <b>Save</b>. Fix anything the console marks red.</>} />
        <Act title={<>Open <b>Publishing overview</b> in the left menu and press <b>Send changes for review</b>.</>} />
      </Actions>
    ),
  };

  const done: GuideStep = {
    id: 'done',
    title: 'All done',
    intro: 'Google reviews a new app in a few days. You get an email when it is live.',
    body: (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
          <IconCheck className="h-7 w-7" />
        </span>
        <p className="max-w-md text-sm text-content-muted">
          If the console still shows something unfinished on the Dashboard, open that task and compare it with the step of the same name here.
        </p>
      </div>
    ),
  };

  // App Store Connect has no such forms: an iOS package is texts, images and the build.
  if (pkg.app.platform !== 'android') {
    const plain = (s: GuideStep): GuideStep => ({ ...s, where: undefined });
    return [start, plain(listing), ...(translations ? [plain(translations)] : []), {
      id: 'build', title: 'Build', body: <BuildDownload token={token} pkg={pkg} />,
    }, done];
  }

  return [
    start, create, privacy, access, ads, contentRating, audience, dataSafety, declarations,
    settings, listing, ...(translations ? [translations] : []), release, done,
  ];
}
